import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import SalonService from "../../services/SalonService";
import { toast } from "react-toastify";
import "./AdvancePaymentWeb.css";
import "../../../src/style.css";

export default function AdvancePaymentWeb() {
    const navigate = useNavigate();
    const location = useLocation();
    const { customerId, salonUuid, bookingRef, amount } = location.state || {};
    const [upiUrl, setUpiUrl] = useState("");
    const [utr, setUtr] = useState("");
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (salonUuid && bookingRef && amount) {
            loadUpi();
        }
    }, [salonUuid, bookingRef, amount]);

    /* ---------------- Load UPI ---------------- */

    const upiId = useMemo(() => {
        if (!upiUrl) return "";
        const match = upiUrl.match(/pa=([^&]+)/);
        return match ? decodeURIComponent(match[1]) : "";
    }, [upiUrl]);

    const maskedUpiId = useMemo(() => {
        if (!upiId || !upiId.includes("@")) return upiId;

        const [name, bank] = upiId.split("@");
        if (name.length <= 4) return `****@${bank}`;

        return `${name.slice(0, 2)}****@${bank}`;
    }, [upiId]);


    const loadUpi = async () => {
        try {
            setLoading(true);

            const res = await SalonService.getUpiPaymentLink({
                customerId,
                salonUuid,
                bookingRef,
                amount,
                note: `Advance for booking ${bookingRef}`,
            });

            setUpiUrl(res?.data?.upiUrl || "");

        } catch {
            toast.error("Unable to generate UPI payment details");
        } finally {
            setLoading(false);
        }
    };


    /* ---------------- Actions ---------------- */

    const copyUpiDetails = async () => {
        if (!upiId) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(upiId);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = upiId;
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }

            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copy not supported on this device");
        }
    };

    const submitUtr = async () => {
        if (!utr || utr.trim().length < 8) {
            toast.error("Please enter a valid UTR / Transaction ID");
            return;
        }

        try {
            setLoading(true);
            await SalonService.updateUpiPayment({
                salonUuid,
                bookingRef,
                utr: utr.trim(),
                amount,
            });
            toast.success("Payment submitted for verification");
            navigate("/home");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- Guards ---------------- */

    if (!salonUuid || !bookingRef || !amount) {
        return (
            <div className="payment-error">
                <h3>Invalid payment link</h3>
                <p>Please open the payment page from your appointment.</p>
                <button onClick={() => navigate("/home")}>Go Back</button>
            </div>
        );
    }

    /* ---------------- UI ---------------- */

    return (
        <div className="payment-container">
            <div className="payment-card">
                <h2 className="payment-title">Advance Payment</h2>

                <div className="amount-box">
                    <span className="currency">₹</span>
                    <span className="amount">{amount}</span>
                </div>

                {upiUrl && (
                    <>
                        <div className="qr-box">
                            <QRCodeSVG value={upiUrl} size={220} level="H" />
                        </div>

                        <p className="hint">
                            Scan the QR using any UPI app
                            {/* (PhonePe / GPay / Paytm) */}
                        </p>
                    </>
                )}

                {upiId && (
                    <div className="upi-id-row">
                        <div className="upi-id-box" onClick={copyUpiDetails}>
                            <span className="upi-id-text">
                                {maskedUpiId}
                            </span>

                            {copied ? (
                                <span className="upi-copied">Copied ✓</span>
                            ) : (
                                <span className="upi-copy-icon" />
                            )}
                        </div>
                    </div>
                )}


                <div className="divider">AFTER PAYMENT</div>

                <input
                    className="utr-input"
                    placeholder="Enter UTR / Transaction ID"
                    value={utr}
                    onChange={(e) => setUtr(e.target.value)}
                />

                <button
                    onClick={submitUtr}
                    className="submit-btn"
                    disabled={loading}
                >
                    {loading ? "Submitting..." : "Submit Payment"}
                </button>

                <p className="note">
                    Payment will be verified by the salon before confirmation.
                </p>
            </div>
        </div>
    );
}
